import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { agentRegister, agentUploadPhoto } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle, UserPlus, Shield, Upload, Eye, EyeOff } from "lucide-react";

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana",
  "Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur",
  "Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana",
  "Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Chandigarh","Puducherry",
  "Jammu and Kashmir","Ladakh","Lakshadweep","Andaman and Nicobar Islands","Dadra and Nagar Haveli and Daman and Diu"
];

export default function AgentRegister() {
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [agreed, setAgreed] = useState(false);
  
  const [form, setForm] = useState({
    name: '', mobile: '', email: '', password: '', confirmPassword: '',
    city: '', state: '', pan_number: '', aadhaar_number: '',
    bank_account: '', ifsc: '', employee_referral_code: ''
  });

  const updateForm = (key, value) => setForm(prev => ({...prev, [key]: value}));

  // Auto-fill the employee referral code from URL query (?ref=CODE).
  // Used by the WhatsApp share link from the Employee Referrals page so that
  // prospective agents don't have to type the code manually.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get('ref') || '').trim().toUpperCase().slice(0, 16);
      if (ref) {
        setForm(prev => ({ ...prev, employee_referral_code: ref }));
      }
    } catch (_) { /* non-fatal */ }
  }, []);

  const isFormValid = () => {
    return (
      form.name.trim() &&
      /^\d{10}$/.test(form.mobile) &&
      form.email.trim() &&
      form.city.trim() &&
      form.state &&
      /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan_number.toUpperCase()) &&
      /^\d{12}$/.test(form.aadhaar_number) &&
      form.bank_account.trim() &&
      form.ifsc.trim() &&
      form.password.length >= 8 &&
      form.password === form.confirmPassword &&
      agreed
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) { toast.error("Please agree to terms and conditions"); return; }
    if (!/^\d{10}$/.test(form.mobile)) { toast.error("Enter a valid 10-digit mobile number"); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (form.password !== form.confirmPassword) { toast.error("Passwords do not match"); return; }
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan_number.toUpperCase())) { toast.error("Invalid PAN format (e.g., ABCDE1234F)"); return; }
    if (!/^\d{12}$/.test(form.aadhaar_number)) { toast.error("Enter valid 12-digit Aadhaar number"); return; }
    
    setLoading(true);
    try {
      // Upload photo if provided
      if (photoFile) {
        const photoForm = new FormData();
        photoForm.append('file', photoFile);
        photoForm.append('mobile', form.mobile);
        try { await agentUploadPhoto(photoForm); } catch (e) { console.error('Photo upload error:', e); }
      }

      await agentRegister({
        name: form.name, mobile: form.mobile, email: form.email,
        password: form.password, city: form.city, state: form.state,
        pan_number: form.pan_number.toUpperCase(), aadhaar_number: form.aadhaar_number,
        bank_account: form.bank_account, ifsc: form.ifsc.toUpperCase(),
        employee_referral_code: form.employee_referral_code.trim().toUpperCase() || null,
      });
      setStep('success');
      toast.success("Application submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#EEF2FF] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 text-center max-w-md w-full">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h2 className="text-2xl font-black text-[#0A2540] mb-3" style={{fontFamily:'Outfit, sans-serif'}}>Application Submitted Successfully</h2>
          <p className="text-gray-600 text-sm leading-relaxed mb-2">
            Thank you for applying to become a Rebuilt agent.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-2">
            Our team will review your application and get back to you within 24 hours.
          </p>
          <p className="text-xs text-gray-400 mb-8">
            You will be able to log in once your application is approved.
          </p>
          <div className="flex flex-col gap-3">
            <Button onClick={() => navigate('/agent/login')} className="bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-full py-3 font-semibold">
              Go to Agent Login
            </Button>
            <Button onClick={() => navigate('/')} variant="outline" className="rounded-full py-3">
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#EEF2FF]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50 px-4 sm:px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-800"><ArrowLeft className="h-5 w-5" /></button>
          <div>
            <h1 className="text-lg font-black text-[#0A2540]" style={{fontFamily:'Outfit, sans-serif'}}>Become a Rebuilt Agent</h1>
            <p className="text-xs text-gray-500">Join our network and earn commissions</p>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Info Banner */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <UserPlus className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-blue-900">Fill in all details below to apply</p>
            <p className="text-xs text-blue-700 mt-0.5">Your application will be reviewed within 24 hours</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Personal Details */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4">Personal Details</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-600 font-semibold">Full Name *</Label>
                <Input data-testid="agent-name" value={form.name} onChange={e => updateForm('name', e.target.value)} placeholder="Your full name" required className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-xs text-gray-600 font-semibold">Mobile Number *</Label>
                <div className="flex gap-2 mt-1">
                  <span className="bg-gray-100 rounded-xl px-3 flex items-center text-sm text-gray-600 font-semibold">+91</span>
                  <Input data-testid="agent-mobile" value={form.mobile} onChange={e => updateForm('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="Enter 10-digit number" maxLength={10} required className="rounded-xl" />
                </div>
                {form.mobile && !/^\d{10}$/.test(form.mobile) && (
                  <p className="text-xs text-red-500 mt-1">Enter a valid 10-digit mobile number</p>
                )}
              </div>
              <div>
                <Label className="text-xs text-gray-600 font-semibold">Email Address *</Label>
                <Input data-testid="agent-email" type="email" value={form.email} onChange={e => updateForm('email', e.target.value)} placeholder="email@example.com" required className="rounded-xl mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-600 font-semibold">City *</Label>
                  <Input data-testid="agent-city" value={form.city} onChange={e => updateForm('city', e.target.value)} placeholder="City" required className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-semibold">State *</Label>
                  <select data-testid="agent-state" value={form.state} onChange={e => updateForm('state', e.target.value)} required className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm mt-1">
                    <option value="">Select</option>
                    {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* KYC Details */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4 flex items-center gap-2"><Shield className="h-4 w-4" /> KYC Details</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-600 font-semibold">PAN Number *</Label>
                <Input data-testid="agent-pan" value={form.pan_number} onChange={e => updateForm('pan_number', e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} required className="rounded-xl mt-1 uppercase" />
                {form.pan_number && form.pan_number.length === 10 && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(form.pan_number.toUpperCase()) && (
                  <p className="text-xs text-red-500 mt-1">Invalid PAN format (e.g., ABCDE1234F)</p>
                )}
              </div>
              <div>
                <Label className="text-xs text-gray-600 font-semibold">Aadhaar Number *</Label>
                <Input data-testid="agent-aadhaar" value={form.aadhaar_number} onChange={e => updateForm('aadhaar_number', e.target.value.replace(/\D/g, '').slice(0, 12))} placeholder="12-digit Aadhaar" maxLength={12} required className="rounded-xl mt-1" />
              </div>
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4">Bank Details</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-600 font-semibold">Bank Account Number *</Label>
                <Input data-testid="agent-bank" value={form.bank_account} onChange={e => updateForm('bank_account', e.target.value)} placeholder="Account number" required className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-xs text-gray-600 font-semibold">IFSC Code *</Label>
                <Input data-testid="agent-ifsc" value={form.ifsc} onChange={e => updateForm('ifsc', e.target.value.toUpperCase())} placeholder="e.g., HDFC0001234" maxLength={11} required className="rounded-xl mt-1 uppercase" />
              </div>
            </div>
          </div>

          {/* Profile Photo */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4">Profile Photo (Optional)</h3>
            <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border border-dashed border-gray-300 hover:border-[#1565C0] transition-colors">
              <Upload className="h-5 w-5 text-gray-400" />
              <span className="text-sm text-gray-600">{photoFile ? photoFile.name : "Upload a photo"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={e => setPhotoFile(e.target.files[0])} />
            </label>
          </div>

          {/* Password */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4">Set Password</h3>
            <div className="space-y-3">
              <div className="relative">
                <Label className="text-xs text-gray-600 font-semibold">Password * (min 8 characters)</Label>
                <div className="relative mt-1">
                  <Input data-testid="agent-password" type={showPassword ? "text" : "password"} value={form.password} onChange={e => updateForm('password', e.target.value)} placeholder="Create password" required minLength={8} className="rounded-xl pr-10" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {form.password && form.password.length < 8 && (
                  <p className="text-xs text-red-500 mt-1">Password must be at least 8 characters</p>
                )}
              </div>
              <div>
                <Label className="text-xs text-gray-600 font-semibold">Confirm Password *</Label>
                <Input data-testid="agent-confirm-password" type="password" value={form.confirmPassword} onChange={e => updateForm('confirmPassword', e.target.value)} placeholder="Confirm password" required className="rounded-xl mt-1" />
                {form.confirmPassword && form.password !== form.confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
              </div>
            </div>
          </div>

          {/* Employee Referral (Optional) */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Employee Referral Code (Optional)</h3>
            <p className="text-[11px] text-gray-500 mb-3">If a Rebuilt employee referred you, enter their code below.</p>
            <Input
              data-testid="agent-employee-ref-code"
              value={form.employee_referral_code}
              onChange={e => updateForm('employee_referral_code', e.target.value.toUpperCase().slice(0, 16))}
              placeholder="e.g., EMP-ABC123"
              className="rounded-xl uppercase"
            />
          </div>

          {/* Terms */}
          <label className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
            <input data-testid="agent-terms" type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-0.5 rounded" />
            <span className="text-sm text-gray-600">I agree to the <a href="/terms" target="_blank" className="text-[#1565C0] hover:underline font-semibold">terms and conditions</a> of Rebuilt</span>
          </label>

          <Button data-testid="agent-submit" type="submit" disabled={loading || !isFormValid()} className="w-full bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-full py-4 font-semibold text-base disabled:opacity-50">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit Application"}
          </Button>

          <p className="text-center text-xs text-gray-500">
            Already have an account? <button type="button" onClick={() => navigate('/agent/login')} className="text-[#1565C0] hover:underline font-semibold">Login here</button>
          </p>
        </form>
      </div>
    </div>
  );
}
